import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Chip,
  IconButton,
  Typography,
  FormControlLabel,
  Switch,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';

interface DeckFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    title: string;
    description: string;
    sideNames: string[];
    tags: string[];
    isPublic: boolean;
  }) => void;
  initialData?: {
    title: string;
    description: string;
    sideNames: string[];
    tags: string[];
    isPublic?: boolean;
  };
  deckId?: string;
}

const DeckForm: React.FC<DeckFormProps> = ({ open, onClose, onSave, initialData, deckId }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sideNames, setSideNames] = useState<string[]>(['Front', 'Back']);
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [isPublic, setIsPublic] = useState(false);

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title);
      setDescription(initialData.description);
      setSideNames(initialData.sideNames);
      setTags(initialData.tags);
      setIsPublic(initialData.isPublic || false);
    } else {
      setTitle('');
      setDescription('');
      setSideNames(['Front', 'Back']);
      setTags([]);
      setIsPublic(false);
    }
  }, [initialData, open]);

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleDeleteTag = (tagToDelete: string) => {
    setTags(tags.filter(tag => tag !== tagToDelete));
  };

  const handleAddSide = () => {
    setSideNames([...sideNames, '']);
  };

  const handleDeleteSide = (index: number) => {
    if (sideNames.length > 2) {
      setSideNames(sideNames.filter((_, i) => i !== index));
    }
  };

  const handleSave = () => {
    if (!title.trim() || !description.trim() || sideNames.some(side => !side.trim())) {
      return;
    }
    onSave({
      title: title.trim(),
      description: description.trim(),
      sideNames: sideNames.map(side => side.trim()),
      tags,
      isPublic
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initialData ? t('decks.editDeck') : t('decks.createDeck')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label={t('formLabels.title')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label={t('formLabels.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
          />
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2
          }}>
            <Tooltip title={isPublic ? 
              t('decks.publicDeckTooltip') : 
              t('decks.privateDeckTooltip')}>
              <Box sx={{ mr: 1 }}>
                {isPublic ? <PublicIcon color="primary" /> : <LockIcon />}
              </Box>
            </Tooltip>
            <FormControlLabel
              control={
                <Switch
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1">
                    {isPublic ? t('decks.deckTypes.publicDeck') : t('decks.deckTypes.privateDeck')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isPublic 
                      ? t('decks.publicDeckDescription')
                      : t('decks.privateDeckDescription')}
                  </Typography>
                </Box>
              }
              sx={{ margin: 0 }}
            />
          </Box>
          
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              {t('decks.cardSides')}
            </Typography>
            {sideNames.map((side, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  label={`${t('decks.side')} ${index + 1}`}
                  value={side}
                  onChange={(e) => {
                    const newSideNames = [...sideNames];
                    newSideNames[index] = e.target.value;
                    setSideNames(newSideNames);
                  }}
                  fullWidth
                  required
                />
                {sideNames.length > 2 && (
                  <IconButton
                    onClick={() => handleDeleteSide(index)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddSide}
              sx={{ mt: 1 }}
            >
              {t('decks.addSide')}
            </Button>
          </Box>
          
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              {t('decks.tags')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                label={t('decks.newTag')}
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                fullWidth
              />
              <Button onClick={handleAddTag}>{t('common.add')}</Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => handleDeleteTag(tag)}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          onClick={handleSave}
          variant="contained"
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeckForm; 